const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const API_VERSION = '/api/v1';

// Token management
export const getToken = () => localStorage.getItem('token');
export const getRefreshToken = () => localStorage.getItem('refresh_token');
export const setTokens = (token, refreshToken) => {
  localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
};
export const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
};

// Demo mode (for development)
export const enableDemo = () => localStorage.setItem('demo_mode', 'true');
export const disableDemo = () => localStorage.removeItem('demo_mode');
export const isDemoMode = () => localStorage.getItem('demo_mode') === 'true';

// Helper to get auth headers
const authHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

// Single in-flight refresh promise so multiple 401s don't all trigger refresh
let refreshPromise = null;

/**
 * Fetch with auth. On 401, tries to refresh the token once and retries the request.
 * If refresh fails, dispatches zm:logout and throws. Skip in demo mode.
 */
async function fetchWithAuth(url, options = {}) {
  const mergeAuth = (opts) => ({
    ...opts,
    headers: { ...authHeaders(), ...opts?.headers },
  });

  const doOne = () => fetch(url, mergeAuth(options));

  let res = await doOne();

  if (res.status === 401 && !isDemoMode() && options._skipRefresh !== true) {
    try {
      if (!refreshPromise) {
        refreshPromise = (async () => {
          const data = await auth.refresh();
          const payload = data?.data ?? data;
          const access = payload?.access_token;
          if (access) {
            setTokens(access, payload?.refresh_token ?? getRefreshToken());
          }
        })();
      }
      await refreshPromise;
      refreshPromise = null;
      res = await doOne();
    } catch (e) {
      refreshPromise = null;
      window.dispatchEvent(new Event('zm:logout'));
      throw e;
    }
  }

  return res;
}

// Parse API error response and throw with backend message/code
const parseAuthError = async (response) => {
  let body;
  try {
    body = await response.json();
  } catch {
    throw new Error(response.status === 401 ? 'Invalid email or password.' : 'Login failed.');
  }
  const msg = body.message || body.error || (response.status === 401 ? 'Invalid email or password.' : 'Login failed.');
  const err = new Error(typeof msg === 'string' ? msg : 'Login failed.');
  if (body.error && typeof body.error === 'string') err.code = body.error;
  err.status = response.status;
  throw err;
};

// Auth API
export const auth = {
  login: async (email, password) => {
    const response = await fetch(`${API_URL}${API_VERSION}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!response.ok) await parseAuthError(response);
    const json = await response.json();
    return json;
  },

  register: async (userData) => {
    const response = await fetch(`${API_URL}${API_VERSION}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    if (!response.ok) throw new Error('Registration failed');
    return response.json();
  },

  refresh: async () => {
    const response = await fetch(`${API_URL}${API_VERSION}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: getRefreshToken() })
    });
    if (!response.ok) throw new Error('Token refresh failed');
    return response.json();
  },

  logout: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/auth/logout`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Logout failed');
    return response.json();
  },

  changePassword: async (oldPassword, newPassword) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || body.error || 'Failed to change password');
    }
    return response.json();
  },

  forgotPassword: async (email) => {
    const response = await fetch(`${API_URL}${API_VERSION}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Request failed');
    }
    return response.json();
  },

  resetPassword: async (token, newPassword) => {
    const response = await fetch(`${API_URL}${API_VERSION}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to reset password');
    }
    return response.json();
  }
};

// Reviews API
export const reviewsApi = {
  getApproved: async (limit = 6) => {
    const response = await fetch(`${API_URL}${API_VERSION}/reviews?page=1&limit=${limit}`);
    if (!response.ok) throw new Error('Failed to fetch approved reviews');
    const data = await response.json();
    return data.data || [];
  },

  getAll: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/reviews?page=1&limit=100`);
    if (!response.ok) throw new Error('Failed to fetch reviews');
    const data = await response.json();
    return data.data || [];
  },

  create: async (reviewData) => {
    const response = await fetch(`${API_URL}${API_VERSION}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reviewData)
    });
    if (!response.ok) throw new Error('Failed to create review');
    const result = await response.json();
    return { data: result.data };
  },

  approve: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/reviews/${id}/approve`, {
      method: 'PATCH',
    });
    if (!response.ok) throw new Error('Failed to approve review');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/reviews/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete review');
    return response.json();
  },

  all: async (params = {}) => {
    const limit = params.limit != null ? params.limit : 100;
    const page = params.page != null ? params.page : 1;
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/reviews?page=${page}&limit=${limit}`);
    if (!response.ok) throw new Error('Failed to fetch reviews');
    return response.json();
  }
};

// Alias for components that import "reviews as reviewsApi"
export const reviews = reviewsApi;

// Quotes API
export const quotes = {
  calculate: async (quoteData) => {
    const response = await fetch(`${API_URL}${API_VERSION}/quotes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(quoteData)
    });
    if (!response.ok) throw new Error('Failed to calculate quote');
    return response.json();
  },

  create: async (quoteData) => {
    const response = await fetch(`${API_URL}${API_VERSION}/quotes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(quoteData)
    });
    if (!response.ok) throw new Error('Failed to create quote');
    return response.json();
  },

  getMyQuotes: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/quotes/my`);
    if (!response.ok) throw new Error('Failed to fetch quotes');
    return response.json();
  },

  getById: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/quotes/${id}`);
    if (!response.ok) throw new Error('Failed to fetch quote');
    return response.json();
  }
};

// Bookings API
export const bookings = {
  create: async (bookingData) => {
    const token = getToken();
    const response = await (token ? fetchWithAuth : fetch)(`${API_URL}${API_VERSION}/bookings`, {
      method: 'POST',
      ...(token ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: JSON.stringify(bookingData)
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = formatApiError(body, 'Failed to create booking');
      const err = new Error(msg);
      err.response = { status: response.status, data: body };
      throw err;
    }
    return body;
  },

  getByConfirmation: async (confirmation) => {
    const response = await fetch(`${API_URL}${API_VERSION}/bookings/track/${encodeURIComponent(confirmation)}`);
    if (!response.ok) throw new Error('Failed to fetch booking');
    return response.json();
  },

  listByEmail: async (email, page = 1, limit = 20) => {
    const params = new URLSearchParams({ email: String(email || '').trim(), page, limit });
    const response = await fetch(`${API_URL}${API_VERSION}/bookings/by-email?${params}`);
    if (!response.ok) throw new Error('Failed to fetch bookings');
    return response.json();
  },

  getMyBookings: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/bookings/my`);
    if (!response.ok) throw new Error('Failed to fetch bookings');
    return response.json();
  },

  getById: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/bookings/${id}`);
    if (!response.ok) throw new Error('Failed to fetch booking');
    return response.json();
  },

  updateBilling: async (id, payload) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/bookings/${id}/billing`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = body.error ?? body.message ?? 'Failed to save billing';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save billing');
    }
    return body;
  },

  updateStatus: async (id, status) => {
    const value = status && typeof status === 'object' ? status.status : status;
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/bookings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: value }),
    });
    if (!response.ok) throw new Error('Failed to update booking status');
    return response.json();
  },

  confirmQuote: async (id, hourly_rate) => {
    const bodyPayload = hourly_rate != null && hourly_rate !== ''
      ? { hourly_rate: Number(hourly_rate) }
      : {};
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/bookings/${id}/confirm-quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyPayload),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || body.message || 'Failed to confirm quote');
    }
    return body;
  },

  /**
   * Cancel a booking by reference and email (public).
   * Backend must: verify email matches, enforce 2-hour rule, unschedule mover, set status to cancelled, send emails.
   */
  cancelByReference: async (confirmationNumber, email) => {
    const response = await fetch(`${API_URL}${API_VERSION}/bookings/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        confirmation_number: confirmationNumber.trim(),
        email: email.trim().toLowerCase()
      })
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = body.error ?? body.message ?? 'Failed to cancel booking';
      const err = new Error(typeof msg === 'string' ? msg : 'Failed to cancel booking');
      err.response = { status: response.status, data: body };
      throw err;
    }
    return body;
  },

  /**
   * Assign a booking to a mover. Backend should create a job for the mover,
   * block the mover's availability for that time, and send a notification.
   */
  assignMover: async (id, moverId) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/bookings/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ mover_id: moverId }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(formatApiError(body, 'Failed to assign mover'));
    }
    return response.json();
  }
};

// Contact API (public form submission)
export const contactApi = {
  submit: async (data) => {
    const response = await fetch(`${API_URL}${API_VERSION}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        phone: data.phone,
        subject: data.subject,
        message: data.message
      })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const msg = err.error || (Array.isArray(err.error) ? err.error[0]?.message : null) || err.message || 'Failed to send message';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to send message');
    }
    return response.json();
  }
};

// Jobs API
export const jobs = {
  getMyJobs: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/my`);
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return response.json();
  },

  mine: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.month != null) searchParams.set('month', params.month);
    if (params.page != null) searchParams.set('page', params.page);
    if (params.limit != null) searchParams.set('limit', params.limit);
    if (params.status) searchParams.set('status', params.status);
    if (params.search) searchParams.set('search', params.search);
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    const query = searchParams.toString();
    const url = `${API_URL}${API_VERSION}/jobs/my${query ? `?${query}` : ''}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return response.json();
  },

  getById: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}`);
    if (!response.ok) throw new Error('Failed to fetch job');
    return response.json();
  },

  get: function (id) { return this.getById(id); },

  start: function (id) { return this.startTimer(id); },
  stop: function (id) { return this.stopTimer(id); },

  startTimer: async (id, body = {}) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: Object.keys(body).length ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) throw new Error('Failed to start timer');
    return response.json();
  },

  stopTimer: async (id, body = {}) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: Object.keys(body).length ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) throw new Error('Failed to stop timer');
    return response.json();
  },

  updateBillingDetails: async (id, { travel_time_hours, packing_supplies_amount }) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/billing-details`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        travel_time_hours: travel_time_hours,
        packing_supplies_amount: packing_supplies_amount,
      }),
    });
    if (!response.ok) throw new Error('Failed to update billing details');
    return response.json();
  },

  updateTimes: async (id, { start_time, end_time }) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/times`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ start_time: start_time || undefined, end_time: end_time || undefined }),
    });
    if (!response.ok) throw new Error('Failed to update times');
    return response.json();
  },

  updateHourlyRate: async (id, hourly_rate) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/hourly-rate`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hourly_rate: Number(hourly_rate) }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to update hourly rate');
    }
    return response.json();
  },

  addCharge: async (id, chargeData) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/charges`, {
      method: 'POST',
      body: JSON.stringify(chargeData),
    });
    if (!response.ok) throw new Error('Failed to add charge');
    return response.json();
  },

  removeCharge: async (jobId, chargeId) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${jobId}/charges/${chargeId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to remove charge');
    return response.json();
  },

  addDiscount: async (id, discountData) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/discounts`, {
      method: 'POST',
      body: JSON.stringify(discountData),
    });
    if (!response.ok) throw new Error('Failed to add discount');
    return response.json();
  },

  removeDiscount: async (jobId, discountId) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${jobId}/discounts/${discountId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to remove discount');
    return response.json();
  },

  updatePayment: async (id, paymentData) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/payment`, {
      method: 'PATCH',
      body: JSON.stringify(paymentData),
    });
    if (!response.ok) throw new Error('Failed to update payment');
    return response.json();
  },

  sendPaymentConfirmation: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${id}/send-payment-confirmation`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to send payment confirmation');
    return response.json();
  },

  createRescheduleRequest: async (jobId, { move_date, move_time, reason }) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/jobs/${jobId}/reschedule-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        move_date,
        move_time,
        reason: reason || undefined,
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = body.error ?? body.message ?? 'Failed to submit reschedule request';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to submit reschedule request');
    }
    return body;
  }
};

// Availability API — backend expects query month=YYYY-MM
export const availability = {
  getMonth: async (monthParam) => {
    const monthStr = typeof monthParam === 'string'
      ? monthParam
      : `${monthParam.year}-${String(monthParam.month).padStart(2, '0')}`;
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/movers/availability?month=${encodeURIComponent(monthStr)}`);
    if (!response.ok) throw new Error('Failed to fetch availability');
    return response.json();
  },

  upsert: async (availabilityData) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/movers/availability`, {
      method: 'PUT',
      body: JSON.stringify(availabilityData),
    });
    if (!response.ok) throw new Error('Failed to update availability');
    return response.json();
  }
};

// Config — receipt/company settings (authenticated)
export const configApi = {
  getReceiptConfig: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/config/receipt`);
    if (!response.ok) throw new Error('Failed to fetch receipt config');
    const json = await response.json();
    return json.data || json;
  }
};

// Site settings (public) — contact email/phone for footer, navbar
export const siteSettingsApi = {
  get: async () => {
    const response = await fetch(`${API_URL}${API_VERSION}/config/site`);
    if (!response.ok) return { contact_email: '', contact_phone: '' };
    const json = await response.json();
    return json.data || json;
  },
};

// Services (public) — rates per service for display on Home, Services pages
export const servicesApi = {
  list: async () => {
    const response = await fetch(`${API_URL}${API_VERSION}/services`);
    if (!response.ok) throw new Error('Failed to fetch service rates');
    const data = await response.json();
    return data.data || [];
  }
};

// Scheduling (public) — available time slots based on mover availability
export const schedulingApi = {
  getAvailableSlots: async (date) => {
    const dateStr = typeof date === 'string' ? date.split('T')[0] : date;
    const response = await fetch(`${API_URL}${API_VERSION}/scheduling/available-slots?date=${encodeURIComponent(dateStr)}`);
    if (!response.ok) throw new Error('Failed to fetch available slots');
    const json = await response.json();
    return json.data || [];
  }
};

// Admin API
const buildQuery = (params) => {
  const sp = new URLSearchParams();
  if (params == null) return '';
  if (params.page != null) sp.set('page', params.page);
  if (params.limit != null) sp.set('limit', params.limit);
  if (params.status != null && params.status !== '') sp.set('status', params.status);
  if (params.role != null && params.role !== '') sp.set('role', params.role);
  if (params.search != null && params.search !== '') sp.set('search', params.search.trim());
  const q = sp.toString();
  return q ? `?${q}` : '';
};

export const admin = {
  getDashboard: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/dashboard`);
    if (!response.ok) throw new Error('Failed to fetch dashboard');
    return response.json();
  },

  dashboard: async () => {
    return admin.getDashboard();
  },

  listMovers: async (params = {}) => {
    const query = buildQuery({ page: params.page || 1, limit: params.limit || 100, status: params.status });
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/movers${query}`);
    if (!response.ok) throw new Error('Failed to fetch movers');
    return response.json();
  },

  movers: async (params = {}) => {
    return admin.listMovers(params);
  },

  approveMover: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/movers/${id}/approve`, {
      method: 'PATCH',
    });
    if (!response.ok) throw new Error('Failed to approve mover');
    return response.json();
  },

  rejectMover: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/movers/${id}/reject`, {
      method: 'PATCH',
    });
    if (!response.ok) throw new Error('Failed to reject mover');
    return response.json();
  },

  listCustomers: async (params = {}) => {
    const query = buildQuery({ page: params.page || 1, limit: params.limit || 100, role: params.role });
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/customers${query}`);
    if (!response.ok) throw new Error('Failed to fetch customers');
    return response.json();
  },

  customers: async (params = {}) => {
    return admin.listCustomers(params);
  },

  /** GET /admin/all-bookings — fetch all bookings for admin (list) */
  bookings: async (params = {}) => {
    const query = buildQuery({
      page: params.page || 1,
      limit: params.limit || 10,
      status: params.status,
      search: params.search,
    });
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/all-bookings${query}`);
    if (!response.ok) throw new Error('Failed to fetch bookings');
    return response.json();
  },

  getBookingById: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/bookings/${id}`);
    if (!response.ok) throw new Error('Failed to fetch booking');
    return response.json();
  },

  serviceRates: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/service-rates`);
    if (!response.ok) throw new Error('Failed to fetch service rates');
    return response.json();
  },

  updateServiceRates: async (rates) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/service-rates`, {
      method: 'PATCH',
      body: JSON.stringify({ rates }),
    });
    if (!response.ok) throw new Error('Failed to update service rates');
    return response.json();
  },

  listRescheduleRequests: async (params = {}) => {
    const query = buildQuery({ page: params.page || 1, limit: params.limit || 50 });
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/reschedule-requests${query}`);
    if (!response.ok) throw new Error('Failed to fetch reschedule requests');
    return response.json();
  },

  reviewRescheduleRequest: async (id, approve) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/reschedule-requests/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approve }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = body.error ?? body.message ?? 'Failed to review reschedule request';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to review reschedule request');
    }
    return body;
  },

  /**
   * Get movers who are available for a booking's date/time (availability accommodates the job).
   */
  availableMoversForBooking: async (bookingId, forReassign = false) => {
    const q = forReassign ? '?reassign=1' : '';
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/bookings/${bookingId}/available-movers${q}`);
    if (!response.ok) throw new Error('Failed to fetch available movers');
    return response.json();
  },

  getSiteSettings: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/site-settings`);
    if (!response.ok) throw new Error('Failed to fetch site settings');
    const json = await response.json();
    return json.data || json;
  },

  listAdmins: async (params = {}) => {
    const query = buildQuery({
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      role: 'admin',
      search: params.search,
    });
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/customers${query}`);
    if (!response.ok) throw new Error('Failed to fetch admins');
    return response.json();
  },

  createAdmin: async (data) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/admins`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = body.error ?? body.message ?? 'Failed to create admin';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to create admin');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  removeAdmin: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/admins/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) await parseApiError(response, 'Failed to remove admin');
  },

  updateSiteSettings: async (data) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/admin/site-settings`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update site settings');
    const json = await response.json();
    return json.data || json;
  },
};

// Notifications API (for movers)
export const notifications = {
  getMine: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/notifications`);
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  },

  markRead: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/notifications/${id}/read`, {
      method: 'PATCH',
    });
    if (!response.ok) throw new Error('Failed to mark notification as read');
    return response.json();
  },

  markAllRead: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/notifications/read-all`, {
      method: 'PATCH',
    });
    if (!response.ok) throw new Error('Failed to mark all as read');
    return response.json();
  }
};

function formatApiError(body, fallback) {
  if (Array.isArray(body?.error) && body.error.length) {
    const parts = body.error.map((e) => {
      if (!e || typeof e !== 'object') return '';
      const field = e.field || e.Field;
      const message = e.message || e.Message;
      if (field && message) return `${field}: ${message}`;
      return message || '';
    }).filter(Boolean);
    if (parts.length) return parts.join('. ');
  }
  if (typeof body?.error === 'string' && body.error) return body.error;
  if (typeof body?.message === 'string' && body.message) return body.message;
  return fallback;
}

const parseApiError = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  throw new Error(formatApiError(body, fallback));
};

export const users = {
  getMe: async () => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/users/me`);
    if (!response.ok) await parseApiError(response, 'Failed to fetch profile');
    return response.json();
  },

  updateMe: async (data) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/users/me`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) await parseApiError(response, 'Failed to update profile');
    return response.json();
  },

  get: async (id) => {
    const response = await fetchWithAuth(`${API_URL}${API_VERSION}/users/${id}`);
    if (!response.ok) await parseApiError(response, 'Failed to fetch user');
    return response.json();
  },
};
